import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { Box, Grid, makeStyles, Typography } from "@material-ui/core";

import { COLORS } from "../../../../../theme/colors";
import { ShaDowBox } from "../../../../../components/boxes";
import { NoDataCard } from "../../../../../components/parts";
import { HorizontalTabs } from "../../../../../components/horizontalTabs";
import { HorizontalArrowTabs } from "../../../../../components/horizontalArrowTabs";
import {
  getData,
  nFormatter,
  sumFromList,
  formatDatetime,
  currencyFormatter,
} from "../../../../../services";

const useStyles = makeStyles((theme) => ({
  box: {
    backgroundColor: COLORS.darks[0][6],
    marginTop: theme.spacing(1.5),
  },
  grid: {
    padding: `${theme.spacing(0.25)}px ${theme.spacing(2.25)}px `,
  },
}));

const fields = [
  "Rental area:",
  "Rental payment:",
  "Leasing start date:",
  "Term:",
  "Leasing end date:",
  "Number of renewals:",
  "Number of tenants:",
];

export const TenancyDataDetail = ({ tId }) => {
  const classes = useStyles();
  const boxRef = useRef();
  const { id } = useParams();
  const { user } = useSelector((state) => state.auth);
  const currencySymbol = user.currency.symbol;
  const title = "Tenancy Data";
  const [tab, setTab] = useState(0);
  const [unit, setUnit] = useState([]);
  const [units, setUnits] = useState([]);
  const TabsComponent = units.length > 1 ? HorizontalArrowTabs : HorizontalTabs;
  const [blockWidth, setBlockWidth] = useState();

  useEffect(() => {
    getData(`/praas/v2/buildings/${id}/tenants/${tId}/units/`, (results) => {
      const units = results.map((result) => ({
        unit_id: result.unit_id,
        raw_rental_payments: result.rental_payments,
        rental_area: `${nFormatter(result.rental_area)} ${user.unit_system}`,
        rental_payments:
          result.rental_payments !== 0
            ? `${currencyFormatter(result.rental_payments, currencySymbol)}/Mon`
            : "No Data",
        leasing_start_date:
          result.leasing_start !== null
            ? formatDatetime(result.leasing_start, "MMM D, YYYY")
            : "No Data",
        term: result.term !== 0 ? `${result.term} years` : "No Data",
        leasing_end_date: (
          <span style={{ color: new Date() > new Date(result.leasing_end) ? COLORS.red : null }}>
            {result.leasing_end !== null
              ? formatDatetime(result.leasing_end, "MMM D, YYYY")
              : "No Data"}
          </span>
        ),
        number_renewals: result.number_renewals,
        number_tenants: result.number_tenants,
      }));
      setUnits(units);
      if (units.length) setUnit(mixData(units[0]));

      window.addEventListener("resize", getBlockWidth);
      getBlockWidth();
    });

    return () => {
      setUnits([]);
      setUnit([]);
    };
  }, []);

  const getBlockWidth = () => setBlockWidth(boxRef.current.clientWidth);

  const mixData = (unit) => {
    const values = [
      unit.rental_area,
      unit.rental_payments,
      unit.leasing_start_date,
      unit.term,
      unit.leasing_end_date,
      unit.number_renewals,
      unit.number_tenants,
    ];
    return fields.map((field, i) => ({
      name: field,
      value: values[i],
    }));
  };

  const changeTab = (i) => {
    setTab(i);
    setUnit(mixData(units[i]));
  };

  const accumulatedRental = sumFromList(units.map((unit) => unit.raw_rental_payments));

  if (!units.length) return <NoDataCard title={title} />;

  return (
    <ShaDowBox fullHeight>
      <Box style={{ padding: 24 }} ref={boxRef}>
        <Typography variant="h4">{title}</Typography>
        <Box className={classes.box} style={{ maxWidth: blockWidth }}>
          <TabsComponent
            tab={tab}
            setTab={changeTab}
            tabNames={units.map((unit) => unit.unit_id)}
          />
          <Box style={{ padding: "10px 0" }}>
            {unit.map((item, i) => (
              <Grid key={i} container justifyContent="space-between" className={classes.grid}>
                <Typography>{item.name}</Typography>
                <Typography>{item.value}</Typography>
              </Grid>
            ))}
          </Box>
        </Box>
        <Grid container justifyContent="space-between" style={{ marginTop: 14 }}>
          <Typography>Accumulated Rental</Typography>
          <Typography>
            {accumulatedRental
              ? `${currencyFormatter(accumulatedRental, currencySymbol)}/Mon`
              : "No Data"}
          </Typography>
        </Grid>
      </Box>
    </ShaDowBox>
  );
};
